import { useState, useCallback } from 'react';
import useAuthenticatedApi from '../../Authentication/components/hooks/useAuthenticatedApi';

export interface OrgDefaults {
    id: number;
    orgId: number;
    logoUrl: string | null;
    emailBody: string | null;
    emailSubject: string | null;
    emailFromName: string | null;
    textSignature: string | null;
    galleryHeaderBackgroundColor: string;
    galleryHeaderForegroundColor: string;
    galleryBackgroundColor: string;
    galleryForegroundColor: string;
    createdAt: string;
    updatedAt: string;
}

export interface UpdateOrgDefaultsRequest {
    logoUrl?: string | null;
    emailBody?: string | null;
    emailSubject?: string | null;
    emailFromName?: string | null;
    textSignature?: string | null;
    galleryHeaderBackgroundColor?: string;
    galleryHeaderForegroundColor?: string;
    galleryBackgroundColor?: string;
    galleryForegroundColor?: string;
}

export default function useOrgDefaults() {
    const authenticatedApi = useAuthenticatedApi();
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    const getOrgDefaults = useCallback(
        async (orgId: number): Promise<OrgDefaults | null> => {
            setIsLoading(true);
            setError(null);
            try {
                const response = await authenticatedApi.get(
                    `${orgId}/org/settings/branding`,
                );
                return response.data;
            } catch (err) {
                console.error('Error getting org defaults:', err);
                setError('Failed to get org defaults');
                return null;
            } finally {
                setIsLoading(false);
            }
        },
        [authenticatedApi],
    );

    const updateOrgDefaults = useCallback(
        async (
            orgId: number,
            data: UpdateOrgDefaultsRequest,
        ): Promise<OrgDefaults | null> => {
            setError(null);
            try {
                const response = await authenticatedApi.put(
                    `${orgId}/org/settings/branding`,
                    data,
                );
                return response.data;
            } catch (err) {
                console.error('Error updating org defaults:', err);
                setError('Failed to update org defaults');
                return null;
            }
        },
        [authenticatedApi],
    );

    return {
        getOrgDefaults,
        updateOrgDefaults,
        isLoading,
        error,
    };
}
